import { Alert, Button, Space, Table, Typography } from "antd";
import type { TableColumnsType } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { ApiError } from "../app/apiError.ts";
import { useAuth } from "../app/auth.tsx";
import { PAGE_SIZE, todayIso, useTariffs, type Tariff } from "../app/billing.ts";
import { RuleHistoryModal, ScopeSwitch, SumText, ValidityText, VersionActions, VersionStatus, useDeleteVersion } from "./BillingRuleParts.tsx";
import { EndVersionModal } from "./EndVersionModal.tsx";
import { TariffModal } from "./TariffModal.tsx";

async function loadHistory(id: string): Promise<Tariff[]> {
  const { data, error, response } = await api.GET("/billing/tariffs/{id}/history", { params: { path: { id } } });
  if (!data) throw new ApiError(response.status, error);
  return data;
}

/** Tuition per course + grade + living option (spec story 56); boarding and meals are included (bd-3sv). */
export function TariffsTab() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("billing.manageRules");
  const [all, setAll] = useState(false);
  const [page, setPage] = useState(1);
  const tariffs = useTariffs(all ? undefined : todayIso(), page);
  const [editing, setEditing] = useState<Tariff | "new" | undefined>(undefined);
  const [historyOf, setHistoryOf] = useState<Tariff | undefined>(undefined);
  const [endOf, setEndOf] = useState<Tariff | undefined>(undefined);
  const remove = useDeleteVersion((id) => api.DELETE("/billing/tariffs/{id}", { params: { path: { id } } }));

  const keyLabel = (row: Tariff) => t("billing.tariffs.keyLabel", { course: row.courseName, grade: row.grade, option: t(`billing.livingOptions.${row.livingOption}`) });
  const versionColumns: TableColumnsType<Tariff> = [
    { key: "amount", title: t("billing.tariffs.monthlyAmount"), align: "right", render: (_, row) => <SumText value={row.monthlyAmount} /> },
    { key: "validity", title: t("billing.validity.title"), render: (_, row) => <ValidityText version={row} /> },
    { key: "status", title: t("billing.status.title"), render: (_, row) => <VersionStatus version={row} /> },
  ];
  const columns: TableColumnsType<Tariff> = [
    { key: "course", title: t("billing.tariffs.course"), dataIndex: "courseName" },
    { key: "grade", title: t("billing.tariffs.grade"), width: 90, render: (_, row) => t("billing.gradeLabel", { grade: row.grade }) },
    { key: "living", title: t("billing.tariffs.livingOption"), render: (_, row) => t(`billing.livingOptions.${row.livingOption}`) },
    ...versionColumns,
    {
      key: "actions",
      title: t("common.actions"),
      width: 170,
      render: (_, row) => (
        <VersionActions
          version={row}
          label={keyLabel(row)}
          canManage={canManage}
          deleting={remove.isPending && remove.variables === row.id}
          onHistory={() => setHistoryOf(row)}
          onEdit={() => setEditing(row)}
          onDelete={() => remove.mutate(row.id)}
          onEnd={() => setEndOf(row)}
        />
      ),
    },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <Typography.Text type="secondary">{t("billing.tariffs.hint")}</Typography.Text>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "space-between" }}>
        <ScopeSwitch all={all} onChange={(next) => { setAll(next); setPage(1); }} />
        {canManage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing("new")}>
            {t("billing.tariffs.create")}
          </Button>
        )}
      </div>
      {tariffs.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void tariffs.refetch()}>{t("app.retry")}</Button>} />}
      <Table<Tariff>
        rowKey="id"
        size="middle"
        loading={tariffs.isPending}
        columns={columns}
        dataSource={tariffs.data?.items ?? []}
        scroll={{ x: true }}
        locale={{ emptyText: t("billing.tariffs.empty") }}
        pagination={{ current: page, pageSize: PAGE_SIZE, total: Number(tariffs.data?.totalCount ?? 0), showSizeChanger: false, onChange: setPage, hideOnSinglePage: true }}
      />
      {canManage && <TariffModal target={editing} onClose={() => setEditing(undefined)} />}
      {canManage && (
        <EndVersionModal
          version={endOf}
          label={endOf ? keyLabel(endOf) : ""}
          end={(id, validTo) => api.POST("/billing/tariffs/{id}/end", { params: { path: { id } }, body: { validTo } })}
          onClose={() => setEndOf(undefined)}
        />
      )}
      <RuleHistoryModal<Tariff>
        kind="tariffs"
        entityType="Tariff"
        version={historyOf}
        title={historyOf ? t("billing.historyTitle", { name: keyLabel(historyOf) }) : ""}
        columns={versionColumns}
        load={loadHistory}
        onClose={() => setHistoryOf(undefined)}
      />
    </Space>
  );
}
